import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { resetPassword } from '../../services/auth';
import { parlorScreen, useDesign } from '../../designs';
import DesignButton from '../../components/ui/DesignButton';
import Input from '../../components/ui/Input';

const d = parlorScreen.tokens;

export default function ForgotPasswordScreen() {
  const { typography } = useDesign();
  const displayFont = `'${typography.displayFont}', serif`;
  const fs = typography.fontSize;
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);
    try {
      await resetPassword(email);
      setSent(true);
    } catch (err: unknown) {
      const code = err instanceof Error && 'code' in err ? (err as { code: string }).code : '';
      if (code === 'auth/user-not-found') {
        setError('No account found with that email address.');
      } else if (code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else if (code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else {
        // Surface the raw error temporarily for diagnosis
        const message = err instanceof Error ? err.message : String(err);
        setError(`Reset failed: ${message}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', minHeight: '100vh', padding: '32px',
      backgroundColor: d.bgPrimary,
    }}>
      <div style={{ width: '100%', maxWidth: '380px' }}>
        {/* Brand */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{
            fontFamily: displayFont, fontSize: '28px', fontWeight: 700,
            color: d.textPrimary, margin: 0,
          }}>
            KREWE
          </h1>
        </div>

        {/* Divider */}
        <div style={{
          height: '1px', marginBottom: '24px',
          background: `linear-gradient(to right, transparent, rgba(200,146,58,0.25), transparent)`,
        }} />

        {sent ? (
          <div style={{ textAlign: 'center' }}>
            <h2 style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
              letterSpacing: '3px', textTransform: 'uppercase',
              color: d.textSecondary, marginBottom: '16px',
            }}>
              Check Your Email
            </h2>
            <p style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 300,
              color: d.textSecondary, lineHeight: 1.6, marginBottom: '32px',
            }}>
              We sent a reset link to your email
            </p>
            <Link to="/login" style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 500,
              color: d.accent, textDecoration: 'none',
            }}>
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            {/* Screen title */}
            <h2 style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
              letterSpacing: '3px', textTransform: 'uppercase',
              color: d.textSecondary, textAlign: 'center', marginBottom: '24px',
            }}>
              Reset Password
            </h2>

            <form onSubmit={handleSubmit}>
              <Input label="Email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />

              {error && (
                <p style={{
                  color: d.negative, fontFamily: "'Raleway', sans-serif",
                  fontSize: fs.detail, marginBottom: '16px', textAlign: 'center',
                }}>
                  {error}
                </p>
              )}

              <DesignButton tier={2} fullWidth type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Reset Link'}
              </DesignButton>
            </form>

            <div style={{ textAlign: 'center', marginTop: '24px' }}>
              <Link to="/login" style={{
                fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
                color: d.textSecondary, textDecoration: 'none',
              }}>
                Back to sign in
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
